import { useEffect, useRef } from "react";

export function usePictureInPicture({
    enabled,
    videoTrackRef,
    myName = "You",
    micOn,
    camOn,
    isSharing,
    onToggleMic,
    onToggleCam,
    onToggleScreenShare,
    onLeave,
    participantCount = 0,
}) {
    const canvasRef = useRef(null);
    const sourceVideoRef = useRef(null);
    const pipVideoRef = useRef(null);
    const animationFrameRef = useRef(null);
    const ctxRef = useRef(null);

    // ── Draw loop ──────────────────────────────────────────────
    const startRenderLoop = (canvas, ctx, pipVideo) => {
        const render = () => {
            if (!canvas || !ctx) return;

            const W = canvas.width;
            const H = canvas.height;

            // Background
            ctx.fillStyle = "#1a1a2e";
            ctx.fillRect(0, 0, W, H);

            // Draw camera video or avatar
            const src = sourceVideoRef.current;
            if (src && src.readyState >= 2 && !src.paused) {
                try {
                    ctx.save();
                    ctx.scale(-1, 1); // mirror
                    ctx.drawImage(src, -W, 0, W, H);
                    ctx.restore();
                } catch (_) { }
            } else {
                // Avatar gradient background
                const grad = ctx.createLinearGradient(0, 0, W, H);
                grad.addColorStop(0, "#4ADE80");
                grad.addColorStop(1, "#16A34A");
                ctx.fillStyle = grad;

                // Circle avatar
                const cx = W / 2;
                const cy = H / 2 - 20;
                const r = 60;
                ctx.beginPath();
                ctx.arc(cx, cy, r, 0, Math.PI * 2);
                ctx.fill();

                // Initial letter
                ctx.fillStyle = "#fff";
                ctx.font = "bold 56px sans-serif";
                ctx.textAlign = "center";
                ctx.textBaseline = "middle";
                ctx.fillText((myName?.[0] || "Y").toUpperCase(), cx, cy);
            }

            // Bottom bar background
            ctx.fillStyle = "rgba(0,0,0,0.55)";
            ctx.fillRect(0, H - 64, W, 64);

            // Name
            ctx.fillStyle = "#ffffff";
            ctx.font = "bold 18px sans-serif";
            ctx.textAlign = "left";
            ctx.textBaseline = "middle";
            ctx.fillText(myName || "You", 16, H - 32);

            // Participant count
            if (participantCount > 0) {
                ctx.fillStyle = "#aaaaaa";
                ctx.font = "14px sans-serif";
                ctx.textAlign = "right";
                ctx.fillText(
                    `${participantCount} participant${participantCount !== 1 ? "s" : ""}`,
                    W - 16,
                    H - 32,
                );
            }

            // Mic status dot
            ctx.beginPath();
            ctx.arc(16, H - 80, 8, 0, Math.PI * 2);
            ctx.fillStyle = micOn ? "#4ADE80" : "#ef4444";
            ctx.fill();

            // Mic label
            ctx.fillStyle = "#fff";
            ctx.font = "13px sans-serif";
            ctx.textAlign = "left";
            ctx.fillText(micOn ? "Mic on" : "Muted", 30, H - 80);

            // Cam status dot
            ctx.beginPath();
            ctx.arc(W / 2 - 20, H - 80, 8, 0, Math.PI * 2);
            ctx.fillStyle = camOn ? "#4ADE80" : "#ef4444";
            ctx.fill();

            ctx.fillText(camOn ? "Cam on" : "Cam off", W / 2 - 8, H - 80);

            // Sharing badge
            if (isSharing) {
                ctx.fillStyle = "#2196f3";
                ctx.fillRect(W - 130, H - 96, 114, 26);
                ctx.fillStyle = "#fff";
                ctx.font = "bold 13px sans-serif";
                ctx.textAlign = "center";
                ctx.fillText("Presenting", W - 73, H - 83);
            }

            animationFrameRef.current = requestAnimationFrame(render);
        };

        render();
    };

    // ── Media Session API (Chrome: interactive PiP controls) ───
    const setupMediaSession = () => {
        if (!("mediaSession" in navigator)) return;

        navigator.mediaSession.metadata = new MediaMetadata({
            title: "Meeting in progress",
            artist: `${participantCount} participant${participantCount !== 1 ? "s" : ""}`,
            album: "TheGlobalApp Meeting",
        });

        // Mic toggle (mapped to "previoustrack" — visible as a button in PiP)
        try {
            navigator.mediaSession.setActionHandler("previoustrack", () => {
                onToggleMic?.();
            });
        } catch (_) { }

        // Cam toggle (mapped to "nexttrack")
        try {
            navigator.mediaSession.setActionHandler("nexttrack", () => {
                onToggleCam?.();
            });
        } catch (_) { }

        // Stop/Leave (mapped to "stop")
        try {
            navigator.mediaSession.setActionHandler("stop", () => {
                onLeave?.();
            });
        } catch (_) { }
    };

    const clearMediaSession = () => {
        if (!("mediaSession" in navigator)) return;
        try {
            navigator.mediaSession.setActionHandler("previoustrack", null);
            navigator.mediaSession.setActionHandler("nexttrack", null);
            navigator.mediaSession.setActionHandler("stop", null);
        } catch (_) { }
    };

    // ── Main PiP effect ────────────────────────────────────────
    useEffect(() => {
        if (!enabled) {
            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
            if (document.pictureInPictureElement) {
                document.exitPictureInPicture().catch(() => { });
            }
            clearMediaSession();
            return;
        }

        if (!document.pictureInPictureEnabled) {
            console.warn("PiP not supported in this browser");
            return;
        }

        let alive = true;

        const startPiP = async () => {
            try {
                // Canvas
                const canvas = document.createElement("canvas");
                canvas.width = 480;
                canvas.height = 270;
                canvasRef.current = canvas;

                const ctx = canvas.getContext("2d");
                ctxRef.current = ctx;

                // PiP video element (hidden in DOM but needed for PiP API)
                let pipVideo = pipVideoRef.current;
                if (!pipVideo) {
                    pipVideo = document.createElement("video");
                    pipVideo.muted = true;
                    pipVideo.autoplay = true;
                    pipVideo.playsInline = true;
                    pipVideo.style.position = "fixed";
                    pipVideo.style.opacity = "0";
                    pipVideo.style.pointerEvents = "none";
                    pipVideo.style.width = "1px";
                    pipVideo.style.height = "1px";
                    pipVideo.style.bottom = "0";
                    pipVideo.style.right = "0";
                    document.body.appendChild(pipVideo);
                    pipVideoRef.current = pipVideo;
                }

                const stream = canvas.captureStream(30);
                pipVideo.srcObject = stream;

                await pipVideo.play();

                if (!alive) return;

                await pipVideo.requestPictureInPicture();

                if (!alive) return;

                setupMediaSession();
                startRenderLoop(canvas, ctx, pipVideo);

                // Listen for PiP close (user closes the window manually)
                pipVideo.addEventListener(
                    "leavepictureinpicture",
                    () => {
                        if (animationFrameRef.current) {
                            cancelAnimationFrame(animationFrameRef.current);
                            animationFrameRef.current = null;
                        }
                    },
                    { once: true },
                );
            } catch (err) {
                if (err.name !== "AbortError") {
                    console.error("PiP failed:", err);
                }
            }
        };

        startPiP();

        return () => {
            alive = false;

            if (animationFrameRef.current) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }

            if (document.pictureInPictureElement) {
                document.exitPictureInPicture().catch(() => { });
            }

            // Remove hidden pip video from DOM
            if (pipVideoRef.current) {
                pipVideoRef.current.srcObject = null;
                if (pipVideoRef.current.parentNode) {
                    pipVideoRef.current.parentNode.removeChild(pipVideoRef.current);
                }
                pipVideoRef.current = null;
            }

            clearMediaSession();
        };
    }, [enabled]);

    // ── Re-draw when state changes (mic/cam/sharing/count) ─────
    // The render loop reads these from closure so we just need
    // to keep refs updated for values used inside the loop
    const micOnRef = useRef(micOn);
    const camOnRef = useRef(camOn);
    const isSharingRef = useRef(isSharing);
    const participantCountRef = useRef(participantCount);
    const myNameRef = useRef(myName);

    useEffect(() => { micOnRef.current = micOn; }, [micOn]);
    useEffect(() => { camOnRef.current = camOn; }, [camOn]);
    useEffect(() => { isSharingRef.current = isSharing; }, [isSharing]);
    useEffect(() => { participantCountRef.current = participantCount; }, [participantCount]);
    useEffect(() => { myNameRef.current = myName; }, [myName]);

    // Update Media Session metadata when state changes
    useEffect(() => {
        if (!enabled) return;
        setupMediaSession();
    }, [enabled, micOn, camOn, participantCount]);

    // ── Video track attachment ─────────────────────────────────
    useEffect(() => {
        const track = videoTrackRef?.publication?.track;

        if (!track) {
            sourceVideoRef.current = null;
            return;
        }

        const video = document.createElement("video");
        video.muted = true;
        video.autoplay = true;
        video.playsInline = true;

        try {
            if (typeof track.attach === "function") {
                track.attach(video);
            } else if (track.mediaStreamTrack) {
                video.srcObject = new MediaStream([track.mediaStreamTrack]);
            }
            video.play().catch(() => { });
            sourceVideoRef.current = video;
        } catch (err) {
            console.error("Track attach failed:", err);
        }

        return () => {
            try {
                if (typeof track.detach === "function") track.detach(video);
            } catch (_) { }
            video.srcObject = null;
            sourceVideoRef.current = null;
        };
    }, [videoTrackRef]);
}